import { useEffect, useRef, useState } from 'react'
import { Eye, Radio } from 'lucide-react'
import { useAuth } from '@core/auth/useAuth'
import {
  ligarAoCanal,
  type EstadoDoCanal,
  type LigacaoDoCanal,
  type PresenteAoVivo,
} from '@modules/bcf/lib/bcfCanalAoVivo'
import { abrirPartilharSessao } from './partilharSessao'
import { useVigiaDaSessaoPartilhada } from './vigiaDaSessaoPartilhada'
import { useAcompanharNaSessao } from './useAcompanharNaSessao'
import {
  dicaDoIndicador,
  outrasPessoas,
  primeiroNome,
  projetoDoCanalDaSessao,
  quemMaisEsta,
} from './presencaNaSessao'

/** O chip na barra de cima quando a sessão aberta é partilhada: o nome
 *  da sessão e quem mais a tem aberta agora ("Cha139 · com Vítor"). O
 *  ponto fica verde com a ligação de presença viva. Clicar abre a
 *  janela da partilha (com quem está partilhada). Ao lado, com mais
 *  alguém na sessão, "Acompanhar": a câmara e a selecção dessa pessoa
 *  passam para esta janela (useAcompanharNaSessao.ts). */
export default function IndicadorSessaoPartilhada({ compacto = false }: { compacto?: boolean }) {
  const { mode, workspace, user } = useAuth()
  // Só com a sessão de facto aberta; a vigia esquece a marca quando
  // ela já não está (vigiaDaSessaoPartilhada.ts).
  const actual = useVigiaDaSessaoPartilhada(workspace?.id)
  const partilhaId = mode === 'cloud' ? (actual?.partilhaId ?? null) : null
  const [participantes, setParticipantes] = useState<PresenteAoVivo[]>([])
  const [estado, setEstado] = useState<EstadoDoCanal>('a-ligar')
  // O canal vive no efeito; o ref serve a quem manda a vista, que só
  // corre com o canal já ligado.
  const ligacao = useRef<LigacaoDoCanal | null>(null)
  const outras = outrasPessoas(participantes, user?.id ?? null)
  const ligado = estado === 'ligado'
  const { acompanho, comecar, parar, aoReceberVista } = useAcompanharNaSessao(ligacao, ligado, outras)

  useEffect(() => {
    if (!partilhaId) return
    // O canal avisa logo "a ligar" ao abrir, e a lista esvazia-se em
    // qualquer estado que não seja ligado.
    const l = ligarAoCanal({
      projeto: projetoDoCanalDaSessao(partilhaId),
      aoReceber: (m) => {
        if (m.tipo === 'presenca') setParticipantes(m.participantes)
        else if (m.tipo === 'vista') aoReceberVista(m)
      },
      aoMudarEstado: (e) => {
        setEstado(e)
        if (e !== 'ligado') setParticipantes([])
      },
    })
    ligacao.current = l
    return () => {
      l.fechar()
      if (ligacao.current === l) ligacao.current = null
    }
  }, [partilhaId, aoReceberVista])

  if (!partilhaId || !actual || !workspace || !user) return null
  const dica = dicaDoIndicador(actual.nome, outras, estado)

  return (
    <div className="flex min-w-0 shrink items-center gap-1">
    <button
      type="button"
      data-campo="indicador-sessao-partilhada"
      title={dica}
      aria-label={dica}
      onClick={() =>
        void abrirPartilharSessao({
          workspaceId: workspace.id,
          workspaceSlug: workspace.slug,
          workspaceNome: workspace.name,
          utilizadorId: user.id,
          utilizadorNome: user.name || user.email,
        })
      }
      className="flex min-w-0 shrink items-center gap-1 rounded border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
    >
      <span
        className={
          'inline-block h-2 w-2 shrink-0 rounded-full ' +
          (ligado ? 'bg-[var(--color-ok-text)]' : 'bg-[var(--color-text-dim)]')
        }
        aria-hidden
      />
      <Radio size={13} className="shrink-0" aria-hidden />
      {!compacto && (
        <span className="max-w-[16rem] truncate">
          {actual.nome}
          <span className="text-[var(--color-text-dim)]">
            {' · '}
            {ligado ? quemMaisEsta(outras) : 'a ligar…'}
          </span>
        </span>
      )}
      {compacto && outras.length > 0 && <span>{outras.length + 1}</span>}
    </button>
    {acompanho ? (
      <button
        type="button"
        data-campo="parar-de-acompanhar"
        onClick={() => parar()}
        title={`Está a ver o que ${acompanho.nome} vê: a câmara e a selecção. Mexer na câmara também deixa de acompanhar.`}
        className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-0.5 text-xs text-[var(--color-accent)]"
      >
        <Eye size={13} aria-hidden />
        {compacto ? 'Parar' : `A acompanhar ${primeiroNome(acompanho.nome)} · Parar`}
      </button>
    ) : (
      ligado &&
      outras.length > 0 &&
      (outras.length === 1 ? (
        <button
          type="button"
          data-campo="acompanhar"
          onClick={() => comecar(outras[0])}
          title={`Ver o que ${outras[0].nome} vê: a câmara e a selecção passam para esta janela.`}
          className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <Eye size={13} aria-hidden />
          {!compacto && `Acompanhar ${primeiroNome(outras[0].nome)}`}
        </button>
      ) : (
        <select
          data-campo="acompanhar"
          aria-label="Acompanhar alguém na sessão"
          value=""
          onChange={(e) => {
            const p = outras.find((o) => o.utilizador === e.target.value)
            if (p) comecar(p)
          }}
          className="shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-panel)] px-1 py-0.5 text-xs text-[var(--color-text-muted)]"
        >
          <option value="">Acompanhar…</option>
          {outras.map((o) => (
            <option key={o.utilizador} value={o.utilizador}>
              {o.nome}
            </option>
          ))}
        </select>
      ))
    )}
    </div>
  )
}
